import clsx from 'clsx'
import type { ChangeEvent, Dispatch, MouseEvent, SetStateAction } from 'react'

import { FvLinkButton, Icon } from '@fv/client-components'
import { composeStopDetails } from '@fv/client-core'
import type { ClientAddressLookupResult } from '@fv/client-types'
import { fvDate } from '@fv/dates'
import type { StopDateType, StopType } from '@fv/models'

import { AddressBookButton } from '@/features/addresses/AddressBookButton'
import { addressMapper } from '@/features/addresses/addressFuncs'
import type { FormCommodity } from '@/features/commodities/FormCommodity'
import { ItemSummary } from '@/features/commodities/ItemSummary'
import { useQuoteFormActions } from '@/features/quote-request/useQuoteFormState'
import type { ShipmentLocationFormModel } from '@/features/shipment-location-form/types'
import createStopDetailOptions from '@/utils/createStopDetailOptions'
import { getMapUrl } from '@/utils/getMapUrl'

import { AddressInput } from '../../components/inputs/AddressInput'
import { InputAdornment, InputGroup } from '../../components/inputs/InputGroup'
import { FtlLocationAccessorials } from '../../components/inputs/LocationAccessorials'
import { ShipTypeSelector } from '../../components/inputs/ShipTypeSelector'
import { List } from '../../components/List'

type RouteStopConfigProps = {
  isLastStop: boolean
  items: FormCommodity[]
  setDatepickerSequence: Dispatch<SetStateAction<number | null>>
  stop: ShipmentLocationFormModel
  stops: ShipmentLocationFormModel[]
}

export const RouteStopConfig = ({
  isLastStop,
  items,
  setDatepickerSequence,
  stop,
  stops,
}: RouteStopConfigProps) => {
  const { accessorials, id, sequence, stopDate, stopDateType, stopType } = stop
  const { removeStop, setStopData } = useQuoteFormActions()
  const isFirstStop = sequence === 0
  const isMultiStop = stops.length > 2

  function updateStop(data: Partial<ShipmentLocationFormModel>) {
    setStopData(sequence, data)
  }

  function openDatePicker(e?: MouseEvent<HTMLButtonElement | HTMLDivElement>) {
    e?.preventDefault()
    setDatepickerSequence(stop.sequence)
  }

  function setAddress(location: ClientAddressLookupResult | null) {
    updateStop(addressMapper.dtoToFormStop(location, 'truckload'))
  }

  function setStopDetails(e: ChangeEvent<HTMLSelectElement>) {
    const details = e.target.value
    let stopDateType: StopDateType = 'on'
    let stopType: StopType = 'pickup'

    if (details.includes('by')) stopDateType = 'by'
    if (details.includes('Deliver')) stopType = 'delivery'
    if (details.includes('and')) stopType = 'both'

    updateStop({
      stopDateType,
      stopType,
    })
  }

  const dayjsDate = stopDate ? fvDate(stopDate) : null
  const month = dayjsDate?.format('MMM')
  const day = dayjsDate?.format('DD')
  const weekday = dayjsDate?.format('ddd')
  const stopDetails = composeStopDetails({ stopDate, stopDateType, stopType })
  const stopDetailOptions = createStopDetailOptions({
    isFirstStop,
    isLastStop,
    stopDate,
  })

  return (
    <div className="flex flex-wrap relative" data-cy={`location-${sequence}`}>
      <div className="flex basis-16 shrink-0 grow-0 max-h-[6.15rem] mr-4">
        {(isFirstStop || isLastStop) &&
          (stopDate ? (
            <button
              type="button"
              className="bg-fv-blue-50 group flex-1 text-center flex flex-col justify-between relative border border-fv-gray"
              data-cy="date-picker"
              onClick={openDatePicker}
            >
              <div className="py-[.3rem] text-[0.9rem] bg-fv-beer-xlight border-b border-fv-gray">
                {month}
              </div>
              <div className="text-fv-blue text-xl group-hover:text-inherit group-focus:text-inherit">
                {day}
              </div>
              <div className="py-[.3rem] text-[0.9rem] bg-fv-beer-xlight border-t border-fv-gray">
                {weekday}
              </div>
            </button>
          ) : (
            <button
              type="button"
              className="flex-1 border border-fv-gray bg-fv-beer-xlight group"
              data-cy="date-picker"
              onClick={openDatePicker}
            >
              <Icon icon="calendar-day" transform="up-1" />
              <div>Date</div>
            </button>
          ))}

        {!isFirstStop &&
          !isLastStop &&
          (stopDate ? (
            <button
              type="button"
              className="group bg-fv-blue-50 group flex-1 text-center flex flex-col justify-between relative border border-fv-gray"
              data-cy="date-picker"
              onClick={openDatePicker}
            >
              <div className="py-[.3rem] text-[0.9rem] bg-fv-beer-xlight border-b border-fv-gray">
                {month}
              </div>
              <div className="text-xl text-fv-blue group-hover:text-inherit group-focus:text-inherit">
                {day}
              </div>
              <div className="py-[.3rem] text-[0.9rem] bg-fv-beer-xlight border-t border-fv-gray">
                {weekday}
              </div>
            </button>
          ) : (
            <button
              type="button"
              className="flex flex-col justify-between items-center group flex-1"
              data-cy="date-picker"
              onClick={openDatePicker}
            >
              <div>Stop</div>
              <div className="text-xl bg-fv-beer-xlight p-2 rounded-full h-9 w-9 border-2 border-fv-gray -mt-[0.2rem] flex items-center justify-center">
                {sequence}
              </div>

              <Icon icon="calendar-day" />
            </button>
          ))}
      </div>
      <div className="mr-8 grid flex-1 grid-cols-12 gap-2">
        <div className="b1650:col-span-12 col-span-8">
          <InputGroup
            inputType="custom"
            inputProps={{ name: `stop-${id}-address` }}
            required
            className="mb-0"
            startContent={
              <>
                <AddressBookButton
                  config={{ context: 'truckload', type: 'shipping' }}
                  setAddress={setAddress}
                />
                <InputAdornment position="start">
                  <FvLinkButton
                    disabled={!stop.postalCode}
                    href={getMapUrl(stop)}
                    icon="map-marker"
                    iconClass={clsx({
                      'text-fv-orange': isFirstStop,
                      'text-fv-green': isLastStop,
                      'text-fv-beer': !isFirstStop && !isLastStop,
                    })}
                    rel="noreferrer"
                    target="_blank"
                  />
                </InputAdornment>
              </>
            }
          >
            <AddressInput
              context="truckload"
              includeAddressBook
              location={stop}
              name={`stop-${id}-address`}
              onLocationChange={setAddress}
              placeholder="i.e. Kansas City, MO or 66040"
              required
            />
          </InputGroup>
        </div>
        <div className="b1600:col-span-5 b1400:col-span-6 b1100:col-span-7 col-span-4 min-w-[17rem]">
          <ShipTypeSelector
            className="-mb-4" // cancels out form-group margin
            value={stop}
            name={`stop-${id}-shiptype`}
            onChange={({ shipType, accessorials }) =>
              updateStop({ shipType, accessorials })
            }
            showLabels={false}
          />
        </div>

        <select
          id={`stop-${id}-details`}
          className="form-control form-control--select col-span-3 min-w-[11rem]"
          onChange={setStopDetails}
          value={stopDetails}
        >
          {stopDetailOptions.map(value => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>

        <FtlLocationAccessorials
          accessorials={accessorials}
          className="flex gap-x-5 mt-2 b1450:ml-0 col-span-9 ml-4 b1650:col-span-full b1650:ml-0 b1150:flex-col b1150:gap-y-3"
          inputNamePrefix={`stop-${id}-`}
          onChange={accessorials => updateStop({ accessorials })}
          openDatePicker={openDatePicker}
        />
      </div>
      <div className="absolute right-0 top-3">
        {!isFirstStop && !isLastStop && (
          <FvLinkButton
            icon="trash"
            iconClass="text-fv-orange-bright"
            onClick={() => removeStop(sequence)}
          />
        )}
      </div>

      {(isMultiStop || !isFirstStop) && !!items.length && (
        <List
          lcp0
          className="basis-full mt-6 pt-2 border-t border-dashed border-fv-gray"
        >
          {items.map(i => (
            <ItemSummary
              item={i}
              key={i.id}
              label={i.pickSequence === sequence ? 'Picking up' : 'Delivering'}
            />
          ))}
        </List>
      )}
    </div>
  )
}
